import config from "../config";
import { getSessionToken } from "./helpers";

let onUnauthorized = () => {};

export const setUnauthorizedHandler = (handler) => {
  onUnauthorized = handler;
};

const publicRequest = { authenticate: false };

export const userLogin = (email, password) =>
  requestApi("/users/login", "POST", { email, password }, publicRequest);

export const lookupInvitation = async (token) => {
  const { invitation } = await requestApi("/invitations/lookup", "POST", { token }, publicRequest);
  return invitation;
};

export const acceptInvitation = (token, password) =>
  requestApi("/invitations/accept", "POST", { token, password }, publicRequest);

export const acceptPasswordReset = (token, password) =>
  requestApi("/passwordResets/accept", "POST", { token, password }, publicRequest);

export const getMe = async () => {
  const { user } = await requestApi("/me");
  return user;
};

export const changePassword = (currentPassword, newPassword) =>
  requestApi("/me/password", "POST", { currentPassword, newPassword });

export const getOrganizations = async () => {
  const { organizations } = await requestApi("/organizations");
  return organizations;
};

export const createOrganization = async (name) => {
  const { organization } = await requestApi("/organizations", "POST", { name });
  return organization;
};

export const getMembers = (organization) =>
  requestApi(`/organizations/${organization}/members`);

export const createInvitation = (organization, email) =>
  requestApi(`/organizations/${organization}/invitations`, "POST", { email });

export const revokeInvitation = (organization, id) =>
  requestApi(`/organizations/${organization}/invitations/${id}`, "DELETE");

export const createPasswordResetLink = async (organization, memberId) => {
  const { token } = await requestApi(
    `/organizations/${organization}/members/${memberId}/passwordReset`,
    "POST"
  );
  return token;
};

export const removeMember = (organization, memberId) =>
  requestApi(`/organizations/${organization}/members/${memberId}`, "DELETE");

export const getBricksForOrganization = async (organization) => {
  const { brickCollections } = await requestApi(
    `/organizations/${organization}/brickCollections`
  );
  return brickCollections;
};

export const getBrickCollection = async (organization, collection) => {
  const { brickCollection } = await requestApi(
    `/organizations/${organization}/brickCollections/${collection}`
  );
  return brickCollection;
};

export const createBrickCollection = async (organization, params) => {
  const { brickCollection } = await requestApi(
    `/organizations/${organization}/brickCollections`,
    "POST",
    params
  );
  return brickCollection;
};

export const updateBrickCollection = async (organization, id, params) => {
  const { brickCollection } = await requestApi(
    `/organizations/${organization}/brickCollections/${id}`,
    "POST",
    params
  );
  return brickCollection;
};

export const deleteBrickCollection = (organization, id) =>
  requestApi(`/organizations/${organization}/brickCollections/${id}`, "DELETE");

export const requestUploads = async (organization, brickCollection, files) => {
  const response = await requestApi("/brickCollections/bricks/uploadRequest", "POST", {
    organization,
    brickCollection,
    files,
  });
  return response.files;
};

const masterImagesPath = (organization, collection) =>
  `/organizations/${organization}/brickCollections/${collection}/masterImages`;

export const createMasterImage = (organization, collection, params) =>
  requestApi(masterImagesPath(organization, collection), "POST", params);

export const saveMasterImageDraft = (organization, collection, id, draft) =>
  requestApi(`${masterImagesPath(organization, collection)}/${id}`, "POST", draft);

export const applyMasterImageRenders = async (organization, collection, id, renders) => {
  const { brickCollection } = await requestApi(
    `${masterImagesPath(organization, collection)}/${id}/apply`,
    "POST",
    { renders }
  );
  return brickCollection;
};

export const deleteMasterImage = async (organization, collection, id) => {
  const { brickCollection } = await requestApi(
    `${masterImagesPath(organization, collection)}/${id}`,
    "DELETE"
  );
  return brickCollection;
};

const httpError = (status, message) => Object.assign(new Error(message), { status });

export const requestApi = async (
  path = "",
  method = "GET",
  data = null,
  { authenticate = true } = {}
) => {
  if (!path.startsWith("/")) {
    path = `/${path}`;
  }

  const headers = { "Content-Type": "application/json" };
  const token = authenticate ? getSessionToken() : null;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${config.domains.api}${path}`, {
    method: method.toUpperCase(),
    mode: "cors",
    cache: "no-cache",
    headers,
    body: data ? JSON.stringify(data) : null,
  });

  if (response.status === 401 && token) {
    onUnauthorized();
    throw httpError(401, "Your session has expired. Please sign in again.");
  }

  if (response.status < 200 || response.status >= 300) {
    const body = await response.json().catch(() => null);
    throw httpError(response.status, body?.error ?? `Request failed (${response.status})`);
  }

  if (response.status === 204) {
    return null;
  }
  return response.json();
};
